import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useUnclaimedTransactions, useUserAccount, useDeals } from "../../CustomHooks/Queries";
import { useRequestCheck, useUpdateAddress } from "../../CustomHooks/Mutations";
import {
  BATTLECARD_FLOW_NAV_STATE,
  buildCheckRequestAddress,
  dispatchErrorToast,
  dispatchSuccessToast,
  getUnclaimedCashBackAmountNumeric,
  getCheckIdFromDealsResponse,
  getUserId,
  isLegacyCheckNotProcessedFromDealsResponse,
} from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";
import {
  addressFromUser,
  buildUpdateAddressPayload,
  formatMailingDisplay,
  isAddressComplete,
  trimAddressFields,
} from "./check-address-utils";
import CheckMailingAddressEditForm, { EditIcon } from "./check-mailing-address-edit-form";
import LegacyCheckPageLayout from "./legacy-check-page-layout";
import "./battle-cards.scss";

export default function CheckMailingAddress() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: userAccountData } = useUserAccount();
  const { data: dealsData } = useDeals();
  const { data: unclaimedTransactionsData } = useUnclaimedTransactions();

  const user = userAccountData?.data?.data?.user;
  const checkAmount = useMemo(
    () => getUnclaimedCashBackAmountNumeric(unclaimedTransactionsData),
    [unclaimedTransactionsData]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [savedAddress, setSavedAddress] = useState(null);
  const [draft, setDraft] = useState(() => addressFromUser(user));

  const addressFromApi = useMemo(() => addressFromUser(user), [user]);
  const activeAddress = savedAddress ?? addressFromApi;
  const mailingAddress = useMemo(() => formatMailingDisplay(isEditing ? draft : activeAddress), [
    isEditing,
    draft,
    activeAddress,
  ]);

  const { mutate: updateAddress, isLoading: isSavingAddress } = useUpdateAddress({
    onSuccess: async () => {
      await queryClient.refetchQueries(APIQueryKeys.userAccount);
      dispatchSuccessToast("Your mailing address has been updated");
      setIsEditing(false);
    },
    onError: (err) => {
      dispatchErrorToast(
        err?.response?.data?.error?.msg ??
          err?.response?.data?.msg ??
          "Unable to update your mailing address. Please try again."
      );
    },
  });

  const { mutate: requestCheck, isLoading: isSubmittingCheck } = useRequestCheck({
    onSuccess: async ({ data }) => {
      await queryClient.refetchQueries(APIQueryKeys.deals);
      await queryClient.refetchQueries(APIQueryKeys.checkConfig);
      dispatchSuccessToast("Your check request has been submitted");
      navigate("/battlecard/check-on-the-way", {
        state: { ...BATTLECARD_FLOW_NAV_STATE, checkAmount: data?.amount },
      });
    },
    onError: async (err) => {
      await queryClient.refetchQueries(APIQueryKeys.deals);

      // A rejected request is surfaced by the check-not-processed screen, which RedirectContext
      // routes to off the refreshed status, so a toast would only duplicate it.
      if (isLegacyCheckNotProcessedFromDealsResponse(queryClient.getQueryData(APIQueryKeys.deals))) {
        return;
      }

      dispatchErrorToast(err?.response?.data?.error ?? "Unable to submit your check request. Please try again.");
    },
  });

  useEffect(() => {
    if (!isEditing) {
      setDraft(activeAddress);
    }
  }, [activeAddress, isEditing]);

  function startEditing() {
    setDraft({ ...activeAddress });
    setIsEditing(true);
  }

  function cancelEditing() {
    setDraft({ ...activeAddress });
    setIsEditing(false);
  }

  function updateDraftField(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function submitCheckRequest(addressFields) {
    const trimmed = trimAddressFields(addressFields);

    if (!isAddressComplete(trimmed)) {
      dispatchErrorToast("Please fill in all required address fields.");
      return;
    }

    requestCheck({
      mobileUserId: getUserId(),
      amount: checkAmount,
      address: buildCheckRequestAddress(trimmed),
    });
  }

  function handleSaveAddress() {
    const trimmed = trimAddressFields(draft);

    if (!isAddressComplete(trimmed)) {
      dispatchErrorToast("Please fill in all required address fields.");
      return;
    }

    updateAddress(
      buildUpdateAddressPayload(trimmed, {
        withdrawalId: getCheckIdFromDealsResponse(dealsData),
      }),
      {
        onSuccess: () => {
          setSavedAddress(trimmed);
        },
      },
    );
  }

  function handleConfirmDisplay() {
    submitCheckRequest(activeAddress);
  }

  return (
    <LegacyCheckPageLayout>
      <div className={`battle-cards-page bc-check-mailing-page${isEditing ? " bc-check-mailing-page--editing" : ""}`}>
        <div className="bc-check-mailing-content">
          {isEditing ? (
            <CheckMailingAddressEditForm
              draft={draft}
              onFieldChange={updateDraftField}
              onSave={handleSaveAddress}
              onCancel={cancelEditing}
              isSaving={isSavingAddress}
            />
          ) : (
            <div className="bc-check-mailing-card">
              <h2 className="bc-check-mailing-title">Confirm your mailing address</h2>
              <p className="bc-check-mailing-subtitle">
                We&apos;ll mail your check to this address once your property is approved.
              </p>

              <div className="bc-check-mailing-address">
                <span className="bc-check-mailing-label">Mailing Address</span>
                <button
                  type="button"
                  className="bc-check-mailing-edit"
                  aria-label="Edit mailing address"
                  onClick={startEditing}
                >
                  <EditIcon />
                </button>

                <p className="bc-check-mailing-line name">{mailingAddress.name}</p>
                <p className="bc-check-mailing-line">{mailingAddress.street}</p>
                <p className="bc-check-mailing-line">{mailingAddress.cityLine}</p>
                <button
                  type="button"
                  className="bc-check-mailing-confirm"
                  onClick={handleConfirmDisplay}
                  disabled={isSubmittingCheck}
                >
                  {isSubmittingCheck ? "Confirming…" : "Confirm"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </LegacyCheckPageLayout>
  );
}
